import Banner from "@/components/Shares/Banner";
import Breadcrumb from "@/components/Shares/Breadcrumb";
import { useState } from "react";
import { isEmpty } from "lodash";
import { IProduct } from "@/interfaces/product";
import { ProductCard } from "@/components/Pages/CategoryListPage";
import { ProductCardSkeleton } from "@/components/Pages/CategoryListPage/components/ProductCard/ProductCard";
import {
  Selection,
  Modal,
  ModalContent,
  useDisclosure,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@nextui-org/react";
import { getProductsOnSale } from "@/service/product";
import ResultSorting from "@/components/Shares/ResultSorting";
import { SearchSideBar } from "@/components/Pages/CategoryListPage";
import EOptionKey from "@/constants/option";
import { FiFilter } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import Head from "next/head";
import { GetStaticProps } from "next";
import SEOHead from "@/components/Shares/SEOHead/SEOHead";
import BreadcrumbSchema from "@/components/Shares/StructuredData/BreadcrumbSchema";

interface SalePageProps {
  title: string;
  description: string;
  products: IProduct[];
}

export default function SaleList(props: SalePageProps) {
  const { title, description, products } = props;
  const [renderProducts, setRenderProducts] = useState<IProduct[]>(products);
  const [filteredProducts, setFilteredProducts] =
    useState<IProduct[]>(products);
  const [category, setCategory] = useState<string>("All");
  const [renderCount, setRenderCount] = useState(products.length);
  const [sorting, setSorting] = useState<Selection>(
    new Set([EOptionKey.relevance]),
  );
  const [inStockFilter, setInStockFilter] = useState<boolean>(false);
  const [saleFilter, setSaleFilter] = useState<boolean>(false);
  const [bestSellerFilter, setBestSellerFilter] = useState<boolean>(false);
  const [newArrivalFilter, setNewArrivalFilter] = useState<boolean>(false);
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

  const handleReset = () => {
    setInStockFilter(false);
    setSaleFilter(false);
    setBestSellerFilter(false);
    setNewArrivalFilter(false);
  };

  // Count the number after filtering
  // ... (You can keep the original useEffect logic for counting renderCount)

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical="https://www.priceworth.com.au/sale"
        keywords="sale furniture, discounted furniture, furniture deals, furniture clearance, affordable furniture, furniture sale Australia"
        ogType="website"
      />
      <BreadcrumbSchema
        items={[
          { label: "Home", link: "/" },
          { label: "Sale", link: "/sale" }
        ]}
      />
      <Banner title="Sale" />
      <div className="container">
        <div className="flex justify-between flex-col items-start mb-8 md:mb-0 md:flex-row md:items-center">
          <Breadcrumb pages={[{ label: "Sale", link: `/sale` }]} />
          <ResultSorting
            products={filteredProducts}
            sorting={sorting}
            setSorting={setSorting}
            setRenderProducts={setRenderProducts}
            renderCount={renderCount}
          />
          <div
            className="flex mt-4 items-center gap-2 lg:hidden select-none"
            onClick={onOpen}
          >
            <FiFilter />
            Filter
          </div>
        </div>
        <div className="flex gap-16">
          {/* filter */}
          <div className="flex-1 hidden lg:block">
            <SearchSideBar
              filteredProducts={filteredProducts}
              setFilteredProducts={setFilteredProducts}
              products={products}
              inStockFilter={inStockFilter}
              setInStockFilter={setInStockFilter}
              omg={false}
              saleFilter={saleFilter}
              setSaleFilter={setSaleFilter}
              bestSellerFilter={bestSellerFilter}
              setBestSellerFilter={setBestSellerFilter}
              newArrivalFilter={newArrivalFilter}
              setNewArrivalFilter={setNewArrivalFilter}
            />
          </div>
          {/* content */}
          <div className="flex-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 w-full transition-all duration-500">
            {renderProducts.map((item) => {
              const { id, productVariants, slug, newArrival, bestSeller } =
                item;
              const hasInStock = productVariants.some(
                (item) => item.totalInventory > 0,
              );
              const onSale = productVariants.some(
                (variant) => variant.oldPrice !== 0,
              );
              if (!hasInStock && inStockFilter) return null;
              if (!onSale && saleFilter) return null;
              if (!bestSeller && bestSellerFilter) return null;
              if (!newArrival && newArrivalFilter) return null;
              if (category !== "All" && item.category.name !== category)
                return null;
              const link = `/product/${slug}`;
              return (
                <div key={id}>
                  {!isEmpty(productVariants) ? (
                    <ProductCard
                      newArrival={newArrival}
                      key={id}
                      link={link}
                      productVariants={productVariants}
                    />
                  ) : (
                    Array.from({ length: 3 }, (_, index) => (
                      <ProductCardSkeleton key={index} />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="full"
        aria-label="Filter"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="p-5 border-b border-icon flex items-center justify-between font-medium">
            <h3 className="text-3xl">Filters</h3>
            <AiOutlineClose
              className="text-3xl text-icon"
              onClick={() => onClose()}
            />
          </ModalHeader>
          <ModalBody className="px-5 py-12 overflow-scroll">
            <SearchSideBar
              filteredProducts={filteredProducts}
              setFilteredProducts={setFilteredProducts}
              products={products}
              inStockFilter={inStockFilter}
              setInStockFilter={setInStockFilter}
              omg={false}
              saleFilter={saleFilter}
              setSaleFilter={setSaleFilter}
              bestSellerFilter={bestSellerFilter}
              setBestSellerFilter={setBestSellerFilter}
              newArrivalFilter={newArrivalFilter}
              setNewArrivalFilter={setNewArrivalFilter}
            />
          </ModalBody>
          <ModalFooter className="p-5 flex justify-between border-t border-icon gap-3">
            <Button
              className="flex-1 h-12 text-lg border border-icon text-icon bg-white"
              radius="none"
              onClick={() => handleReset()}
            >
              Reset
            </Button>
            <Button
              className="flex-1 h-12 text-lg text-white bg-icon"
              radius="none"
              onClick={() => onClose()}
            >
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export const getStaticProps: GetStaticProps<SalePageProps> = async () => {
  const response = await getProductsOnSale();
  return {
    props: {
      title: "Sale | Priceworth Furniture",
      description:
        "Explore the latest sale items at Priceworth Furniture. Find great deals and discounts on a wide range of furniture and homewares. Shop now and save big!",
      products: response.statusCode === 200 ? response.data : [],
    },
    revalidate: 3600,
  };
};
